import React, { useState } from 'react';
import { Link, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { LogOut, Home, Menu as MenuIcon, Star, Users, MessageSquare, Settings, Upload, X, ChevronRight, FileText, Globe, Image, Inbox, Zap, QrCode, Newspaper } from 'lucide-react';

// Import page components
import DashboardHome from './DashboardHome';
import MenuItems from './MenuItems';

// Import admin panel components (extracted from monolith)
import SpecialsAdmin from './components/SpecialsAdmin';
import MessagesAdmin from './components/MessagesAdmin';
import DrawingEntriesAdmin from './components/DrawingEntriesAdmin';
import UsersAdmin from './components/UsersAdmin';
import SettingsAdmin from './components/SettingsAdmin';
import ContactsAdmin from './components/ContactsAdmin';
import PagesAdmin from './components/PagesAdmin';
import HomeEditor from './components/HomeEditor';
import IndexNowAdmin from './components/IndexNowAdmin';
import MediaGallery from './components/MediaGallery';
import SMSInboxAdmin from './components/SMSInboxAdmin';
import AutomationAdmin from './components/AutomationAdmin';
import QRPagesAdmin from './components/QRPagesAdmin';
import BlogAdmin from './components/BlogAdmin';

const AdminDashboard = () => {
  const { user, logout, isSuperAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const closeSidebar = () => setSidebarOpen(false);

  const NavLink = ({ to, icon: Icon, children, emoji }) => (
    <Link
      to={to}
      onClick={closeSidebar}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
        isActive(to) || (to === '/admin/dashboard' && location.pathname === '/admin/dashboard')
          ? 'bg-pink-100 text-pink-600 font-bold'
          : 'hover:bg-gray-100 text-gray-700'
      }`}
    >
      {Icon ? <Icon className="w-5 h-5" /> : <span className="w-5 h-5 text-center">{emoji}</span>}
      {children}
    </Link>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <MenuIcon className="w-6 h-6" />}
            </button>
            
            <div className="bg-white border-2 md:border-4 border-pink-600 rounded-lg px-2 md:px-3 py-1">
              <div className="text-base md:text-xl font-black leading-tight">
                <span className="text-black">1<sup className="text-xs">st</sup> Ave</span>
                {' '}
                <span className="text-black">ICP</span>
              </div>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg md:text-2xl font-black">Admin Dashboard</h1>
              <p className="text-xs md:text-sm text-gray-600 truncate max-w-[200px]">
                {user?.email} {isSuperAdmin && '(Super Admin)'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <Link to="/">
              <Button variant="outline" size="sm" className="gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-4">
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">View Site</span>
              </Button>
            </Link>
            <Button onClick={handleLogout} variant="destructive" size="sm" className="gap-1 md:gap-2 text-xs md:text-sm px-2 md:px-4">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="flex relative">
        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={closeSidebar}
          />
        )}

        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-white border-r min-h-[calc(100vh-57px)] lg:min-h-[calc(100vh-73px)]
          transform transition-transform duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          pt-[57px] lg:pt-0
          overflow-y-auto
        `}>
          <nav className="p-4 md:p-6 space-y-1 md:space-y-2 pb-20">
            <NavLink to="/admin/dashboard" icon={Home}>
              Dashboard
            </NavLink>
            <NavLink to="/admin/dashboard/menu-items" icon={MenuIcon}>
              Menu Items
            </NavLink>
            <NavLink to="/admin/dashboard/specials" icon={Star}>
              Specials
            </NavLink>
            <NavLink to="/admin/dashboard/home-editor" icon={Home}>
              Home Page
            </NavLink>
            <NavLink to="/admin/dashboard/pages" icon={FileText}>
              Pages
            </NavLink>
            <NavLink to="/admin/dashboard/media" icon={Image}>
              Media Gallery
            </NavLink>
            <NavLink to="/admin/dashboard/messages" icon={MessageSquare}>
              Messages
            </NavLink>
            <NavLink to="/admin/dashboard/sms-inbox" icon={Inbox}>
              SMS Inbox
            </NavLink>
            <NavLink to="/admin/dashboard/contacts" icon={Upload}>
              Contacts & CSV
            </NavLink>
            <NavLink to="/admin/dashboard/drawings" emoji="🎰">
              Drawing Entries
            </NavLink>
            {isSuperAdmin && (
              <NavLink to="/admin/dashboard/users" icon={Users}>
                Users
              </NavLink>
            )}
            <NavLink to="/admin/dashboard/settings" icon={Settings}>
              Settings
            </NavLink>
            <NavLink to="/admin/dashboard/indexnow" icon={Globe}>
              IndexNow (SEO)
            </NavLink>
            <NavLink to="/admin/dashboard/automation" icon={Zap}>
              Automation
            </NavLink>
            <NavLink to="/admin/dashboard/qr-pages" icon={QrCode}>
              QR Pages
            </NavLink>
            <NavLink to="/admin/dashboard/blog" icon={Newspaper}>
              Blog / News
            </NavLink>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8 min-w-0 overflow-x-auto">
          <Routes>
            <Route index element={<DashboardHome />} />
            <Route path="menu-items" element={<MenuItems />} />
            <Route path="specials" element={<SpecialsAdmin />} />
            <Route path="pages" element={<PagesAdmin />} />
            <Route path="home-editor" element={<HomeEditor />} />
            <Route path="media" element={<MediaGallery />} />
            <Route path="sms-inbox" element={<SMSInboxAdmin />} />
            <Route path="messages" element={<MessagesAdmin />} />
            <Route path="contacts" element={<ContactsAdmin />} />
            <Route path="drawings" element={<DrawingEntriesAdmin />} />
            <Route path="users" element={<UsersAdmin />} />
            <Route path="settings" element={<SettingsAdmin />} />
            <Route path="indexnow" element={<IndexNowAdmin />} />
            <Route path="automation" element={<AutomationAdmin />} />
            <Route path="qr-pages" element={<QRPagesAdmin />} />
            <Route path="blog" element={<BlogAdmin />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
